import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import { Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { AdvancedMarker, APIProvider, Map, useMap } from "@vis.gl/react-google-maps";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import Icon from "@mdi/react";
import { mdiAlphaHCircleOutline } from "@mdi/js";

import { DefaultPosition, EscalaComprometimento } from "../../config/defines";
import DashboardService from "../../services/DashboardService";
import { DashboardGetMapResponseEntity } from "../../entities/DashboardEntity";
import { escalaCalculo } from "../../config/utils";

type DashboardCardMapProps = {
	questionnaireId?: any;
	clientId?: any;
	unitId?: any;
};

export default function DashboardCardMap(props: DashboardCardMapProps) {
	const dashboardService = new DashboardService();

	const navigate = useNavigate();

	const containerRef = useRef<HTMLDivElement>(null);
	const [formBounds, setFormBounds] = useState<MapBoundsFilter | undefined>(undefined);

	const questionnaireIdRef = useRef<any>(null);
	const clientIdRef = useRef<any>(null);
	const unitIdRef = useRef<any>(null);

	const queryMapa = useQuery(["dashboard", "mapa", props.questionnaireId, props.clientId, props.unitId, formBounds], () => fetchData(props.questionnaireId, props.clientId, props.unitId), {
		enabled: !!formBounds,
		refetchOnWindowFocus: false,
		refetchInterval: 60000,
		keepPreviousData: true,
	});

	async function fetchData(questionnaireId: any, clientId: any, unitId: any) {
		let resp = await dashboardService.getMap(questionnaireId, clientId, unitId, `${formBounds!.east}`, `${formBounds!.north}`, `${formBounds!.south}`, `${formBounds!.west}`);

		if (resp.data && queryMapa.data) {
			if (
				(questionnaireIdRef.current !== null && questionnaireIdRef.current !== questionnaireId) ||
				(clientIdRef.current !== null && clientIdRef.current !== clientId) ||
				(unitIdRef.current !== null && unitIdRef.current !== unitId)
			) {
			} else {
				resp.data = [...queryMapa.data, ...resp.data].filter((item, index, self) => index === self.findIndex((other) => other.id === item.id));
			}
		}

		questionnaireIdRef.current = questionnaireId;
		clientIdRef.current = clientId;
		unitIdRef.current = unitId;

		return resp.data;
	}

	async function handleClick() {
		let sessionFiltro = sessionStorage.getItem("dashboardFiltro");
		if (sessionFiltro) {
			let sessionFiltroJSON = JSON.parse(sessionFiltro);
			navigate(
				`/dashboardMapa?${new URLSearchParams({
					questionnaireLabel: sessionFiltroJSON.questionnaire?.label ?? "",
					questionnaireValue: sessionFiltroJSON.questionnaire?.value ?? "",
					clientLabel: sessionFiltroJSON.client?.label ?? "",
					clientValue: sessionFiltroJSON.client?.value ?? "",
					unitLabel: sessionFiltroJSON.unit?.label ?? "",
					unitValue: sessionFiltroJSON.unit?.value ?? "",
				}).toString()}`
			);
		} else {
			navigate(`/dashboardMapa`);
		}
	}

	return (
		<Card className="border-0 shadow mb-4 overflow-hidden" style={{ width: "100%", height: 300, cursor: "pointer" }} onClick={handleClick}>
			<Card.Body className="p-0">
				<div className={`d-flex position-relative`} style={{ width: "100%", height: "100%" }} ref={containerRef}>
					<APIProvider apiKey={`${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`}>
						<Map
							mapId={"dashboard"}
							defaultCenter={DefaultPosition}
							defaultZoom={10}
							fullscreenControl={false}
							streetViewControl={false}
							zoomControl={false}
							mapTypeControl={false}
							scaleControl={false}
							rotateControl={false}
							headingInteractionEnabled={false}
							scrollwheel={false}
							tiltInteractionEnabled={false}
							draggable={false}
							style={{ width: "100%", height: "100%" }}
						>
							<DashboardMapaMarkers
								markers={queryMapa.data || []}
								onBoundsChange={(bounds) => {
									setFormBounds(bounds);
								}}
								onClusterClick={() => {}}
							/>
						</Map>
					</APIProvider>
				</div>
			</Card.Body>
		</Card>
	);
}

type MapBoundsFilter = {
	center: { lat: number; lng: number };
	zoom: number;
	north: number;
	east: number;
	south: number;
	west: number;
};

type DashboardMapaMarkersProps = {
	base?: { lat: number; lng: number };
	sos?: { lat: number; lng: number };
	markers: DashboardGetMapResponseEntity;
	onBoundsChange: (bounds: MapBoundsFilter) => void;
	onClusterClick: () => void;
};

function DashboardMapaMarkers(props: DashboardMapaMarkersProps) {
	const map = useMap();
	const mapCluster = useMemo(() => {
		let markerCluster = new MarkerClusterer({
			map: map,
			// algorithmOptions: { maxZoom: 30 },
			onClusterClick: (event, cluster, map) => {
				map.fitBounds(cluster.bounds!);
				props.onClusterClick();
			},
		});

		return markerCluster;
	}, []);
	const [markers, setMarkers] = useState<google.maps.marker.AdvancedMarkerElement[]>([]);

	useEffect(() => {
		if (map && mapCluster && markers) {
			mapCluster.clearMarkers();
			mapCluster.addMarkers(Object.values(markers));
		}
	}, [map, mapCluster, markers]);

	useEffect(() => {
		let markersArray = [...markers];
		for (let i = markersArray.length - 1; i >= 0; i--) {
			const marker = markersArray[i];
			const markerTitle = marker.getAttribute("title");

			const exists = props.markers.some((item) => item.id === Number(markerTitle));

			if (!exists) {
				markersArray.splice(i, 1);
			}
		}

		setMarkers(markersArray);
	}, [props.markers]);

	useEffect(() => {
		let onBoundsChangeTimeout: NodeJS.Timeout;

		function boundsChanged() {
			if (map) {
				const newBounds = map.getBounds()!;
				const center = map.getCenter();
				if (newBounds && center) {
					const boundsFilter = {
						center: { lat: center!.lat(), lng: center!.lng() },
						zoom: map.getZoom()!,
						north: newBounds.getNorthEast().lat(),
						east: newBounds.getNorthEast().lng(),
						south: newBounds.getSouthWest().lat(),
						west: newBounds.getSouthWest().lng(),
					};

					clearTimeout(onBoundsChangeTimeout);
					onBoundsChangeTimeout = setTimeout(() => {
						props.onBoundsChange(boundsFilter);
					}, 250);
				}
			}
		}

		if (map) {
			map.addListener("bounds_changed", boundsChanged);

			boundsChanged();

			return () => {
				clearTimeout(onBoundsChangeTimeout);
				google.maps.event.clearListeners(map, "bounds_changed");
			};
		}
	}, [map]);

	return (
		<>
			{props.base && (
				<DashboardMapaMarker id={-1} position={{ lat: props.base.lat, lng: props.base.lng }}>
					<Icon path={mdiAlphaHCircleOutline} size={1.5} className="text-danger bg-white rounded-circle" style={{ filter: "drop-shadow( 0 0 2px rgba(0, 0, 0, 0.5))" }} />
				</DashboardMapaMarker>
			)}
			{props.sos && (
				<DashboardMapaMarker id={-2} position={{ lat: props.sos.lat, lng: props.sos.lng }}>
					<Icon path={mdiAlphaHCircleOutline} size={1.5} className="text-air-blue bg-white rounded-circle" style={{ filter: "drop-shadow( 0 0 2px rgba(0, 0, 0, 0.5))" }} />
				</DashboardMapaMarker>
			)}
			{props.markers.map((marker) => {
				let escala = escalaCalculo(EscalaComprometimento, marker.compromiseIndex * 100);
				return (
					<DashboardMapaMarker
						key={marker.id}
						onRef={(item) => {
							setMarkers((prev) => [...prev, item]);
						}}
						id={marker.id}
						position={{ lat: marker.latitude, lng: marker.longitude }}
					>
						<div
							className="d-flex align-items-center justify-content-center text-white rounded-circle shadow-sm fw-bold"
							style={{
								width: 30,
								height: 30,
								background: marker.compromiseIndex !== undefined ? escala.data.cor : "#03c136",
								textShadow: "0 1px 2px black, 0 -1px 2px black",
								border: "2px dashed black",
							}}
						>
							{marker.compromiseIndex !== undefined ? Math.round(marker.compromiseIndex * 100) : "1"}
						</div>
					</DashboardMapaMarker>
				);
			})}
		</>
	);
}

type DashboardMapaMarkerProps = {
	onRef?: (ref: google.maps.marker.AdvancedMarkerElement) => void;
	id: number;
	position: { lat: number; lng: number };
	children: ReactNode | ReactNode[];
};

function DashboardMapaMarker(props: DashboardMapaMarkerProps) {
	const navigate = useNavigate();

	const markerRef = useCallback((marker: google.maps.marker.AdvancedMarkerElement) => {
		if (marker && props.onRef) {
			props.onRef(marker);
		}
		return marker;
	}, []);

	return (
		<AdvancedMarker
			title={`${props.id}`}
			position={props.position}
			ref={markerRef}
			onClick={() => {
				navigate("/avaliacaoDetalhes/" + props.id);
			}}
		>
			{props.children}
		</AdvancedMarker>
	);
}
